import { useSnackbar } from 'contexts/snackbar';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import apiService from 'services/api';
import assignmentService from 'services/assignment';
import { inferSubmissionTypes } from 'utils/assignmentSubmissionTypes';
import { convertCanvasRubric, convertD2LRubric } from 'utils/rubricScoring';

const settle = (operation) =>
    Promise.resolve()
        .then(operation)
        .then(
            (value) => ({ status: 'fulfilled', value }),
            (reason) => ({ status: 'rejected', reason })
        );

const buildAssignmentLmsPatch = ({ remote, platform, gradingSettings }) => {
    const isD2l = platform === 'D2L';
    if (isD2l) {
        if (!remote?.Id) throw new Error('The LMS assignment did not include an ID.');
        return {
            api_data: remote,
            gradingSettings: {
                ...gradingSettings,
                submission_types: inferSubmissionTypes(remote),
            },
            api_assignment_id: remote.Id,
            title: remote.Name ?? '',
            description: remote.CustomInstructions?.Html ?? '',
            dueDate: remote.DueDate,
            rubric: remote.Assessment?.Rubrics?.length > 0 ? convertD2LRubric(remote.Assessment.Rubrics[0]) : null,
        };
    }
    if (!remote?.id) throw new Error('The LMS assignment did not include an ID.');
    return {
        api_data: remote,
        gradingSettings: {
            ...gradingSettings,
            submission_types: inferSubmissionTypes(remote),
        },
        api_assignment_id: remote.id,
        title: remote.name ?? '',
        description: remote.description ?? '',
        dueDate: remote.due_at,
        rubric: remote.rubric ? convertCanvasRubric(remote.rubric, remote.rubric_settings) : null,
    };
};

const useAssignmentLmsResync = ({
    assignment,
    auth,
    form,
    onSettingsReplaced,
    setForceRteUpdate,
    setBatch,
    setForm,
    setSubmissions,
}) => {
    const { t } = useTranslation('assignment');
    const { message } = useSnackbar();

    return useCallback(async () => {
        if (!auth.course_id || !form.api_assignment_id) return;
        try {
            const remote = await apiService.getAssignment({
                course_id: auth.course_id,
                accessToken: localStorage.getItem('access_token'),
                assignment_id: form.api_assignment_id,
            });

            const formPatch = buildAssignmentLmsPatch({
                remote,
                platform: process.env.REACT_APP_KIND,
                gradingSettings: form.gradingSettings,
            });

            let cleanupError;
            if (assignment?.id) {
                const [submissionsResult, batchResult] = await Promise.all([
                    settle(() => assignmentService.deleteAssignmentSubmissions(assignment.id)),
                    settle(() => assignmentService.deleteAssignmentBatch(assignment.id)),
                ]);
                if (submissionsResult.status === 'fulfilled') setSubmissions([]);
                if (batchResult.status === 'fulfilled') setBatch?.([]);
                const rejected = [submissionsResult, batchResult].find(({ status }) => status === 'rejected');
                cleanupError = rejected?.reason;
            }

            setForm((current) => ({ ...current, ...formPatch }));
            onSettingsReplaced();
            setForceRteUpdate((current) => current + 1);
            if (cleanupError) throw cleanupError;
        } catch (error) {
            message.error(t('message.load-error', 'We could not resync the LMS assignment. Please try again.'));
            return false;
        }
    }, [
        assignment?.id,
        auth.course_id,
        form,
        message,
        onSettingsReplaced,
        setBatch,
        setForceRteUpdate,
        setForm,
        setSubmissions,
        t,
    ]);
};

export default useAssignmentLmsResync;
